import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CaseEventReturn, CaseReturn } from '../clients/rust-api-client';
import { billingService } from '../clients/ApiClients';
import { Badge } from '../common/Catalyst/badge';
import { Button } from '../common/Catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '../common/Catalyst/dialog';
import {
  Description,
  ErrorMessage,
  Field,
  Label,
} from '../common/Catalyst/fieldset';
import { Textarea } from '../common/Catalyst/textarea';
import { Subheading } from '../common/Catalyst/heading';
import { Text } from '../common/Catalyst/text';
import LoadingSpinner from '../LoadingSpinner';
import ShowAlert from '../ShowAlert';
import { ErrorAlert } from '../common/Custom/ErrorAlert';
import {
  billingErrorMessage,
  formatBillingDate,
  formatMoney,
} from '../utils/billingHelpers';
import { caseStatus, collectionsEventType } from './enumLabels';
import { AdvanceCaseDialog } from './CollectionsPanel';

interface Props {
  hoaId: string;
  caseId: string;
  canAdvance: boolean;
  canNote: boolean;
}

/**
 * Where an event sits within a single day.
 *
 * **The API cannot tell us the real order within a day.** `list_events`
 * sorts by `occurred_on` then `id`, and the id is a nanoid rather than
 * a sequence — so several events on one date come back arbitrarily,
 * which is normal (an aging run opens a case, sends a notice and
 * advances it in one pass). `collections_events.date_created` would
 * order them correctly but is neither sorted on nor returned.
 *
 * Until it is, the trail orders by causality rather than pretending:
 * a case is opened before anything happens to it, and resolved after
 * everything else. That cannot be wrong about the day, and it stops the
 * trail showing "advanced to demand" above "opened".
 */
const eventRank = (e: CaseEventReturn): number => {
  if (e.event_type === 'opened') return 0;
  if (e.event_type === 'resolved') return 2;
  return 1;
};

/** Newest day first, and within a day the last thing that happened. */
const compareForTrail = (a: CaseEventReturn, b: CaseEventReturn): number => {
  if (a.occurred_on !== b.occurred_on) {
    return a.occurred_on < b.occurred_on ? 1 : -1;
  }
  return eventRank(b) - eventRank(a);
};

/**
 * One case and everything that has happened to it.
 *
 * **A note never changes the status.** Recording what was agreed on the
 * phone and deciding to escalate are different acts, and only the
 * second is Owner/Admin — so the trail shows which events moved the
 * case and which only described it.
 */
const CaseEventTrail: React.FC<Props> = ({
  hoaId,
  caseId,
  canAdvance,
  canNote,
}) => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<CaseEventReturn[]>([]);
  const [row, setRow] = useState<CaseReturn | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [advancing, setAdvancing] = useState<CaseReturn | null>(null);
  const [noting, setNoting] = useState(false);
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState('');
  // The server's refusal, apart from the field's own "write something":
  // it is not about what was typed, so it does not belong under the box.
  const [noteSaveError, setNoteSaveError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await billingService.listCaseEvents({
        caseId,
        perPage: 200,
      });
      setEvents(data.events);
      // The case itself has no by-id read, so it comes from the list
      // it was reached from.
      const list = await billingService.listCases({
        hoaId,
        perPage: 200,
        includeClosed: true,
      });
      setRow(list.data.cases.find((c) => c.id === caseId) ?? null);
    } catch (err) {
      console.error('Error loading case:', err);
      setError(billingErrorMessage(err, "Couldn't load this case."));
    } finally {
      setLoading(false);
    }
  }, [caseId, hoaId]);

  useEffect(() => {
    void load();
  }, [load]);

  const addNote = async (): Promise<void> => {
    if (!note.trim()) {
      setNoteError('Write what happened.');
      return;
    }
    try {
      setBusy(true);
      setNoteSaveError('');
      await billingService.addCaseNote({
        caseId,
        caseNoteRequest: { note: note.trim() },
      });
      setNoting(false);
      setNote('');
      await load();
    } catch (err) {
      console.error('Error adding note:', err);
      setNoteSaveError(billingErrorMessage(err, "Couldn't add the note."));
    } finally {
      setBusy(false);
    }
  };

  if (loading && events.length === 0) return <LoadingSpinner />;

  const ordered = [...events].sort(compareForTrail);

  return (
    <div>
      <Button
        plain
        onClick={() => navigate(`/hoa/${hoaId}/billing/collections`)}
      >
        ← All cases
      </Button>

      {error && (
        <div className="my-4">
          <ShowAlert
            alertType="error"
            alertTitle="Couldn't load this case"
            alertMessage={error}
            isVisible={!!error}
            onClose={() => setError(null)}
            timeout={10000}
            isInline
          />
        </div>
      )}

      <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Subheading>{row?.home_label ?? 'Collections case'}</Subheading>
          {row && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge color={caseStatus.color(row.status)}>
                {caseStatus.label(row.status)}
              </Badge>
              {row.closed_on && (
                <Badge color="zinc">
                  Closed {formatBillingDate(row.closed_on)}
                </Badge>
              )}
              <Text className="text-sm">
                {row.payer_name ? `${row.payer_name} · ` : ''}
                opened {formatBillingDate(row.opened_on)} at{' '}
                {formatMoney(row.trigger_balance)}
                {row.trigger_days_past_due != null
                  ? `, ${row.trigger_days_past_due} days past due`
                  : ''}
              </Text>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {canNote && !row?.closed_on && (
            <Button outline onClick={() => setNoting(true)}>
              Add a note
            </Button>
          )}
          {canAdvance && row && !row.closed_on && (
            <Button color="red" onClick={() => setAdvancing(row)}>
              Move it on
            </Button>
          )}
        </div>
      </div>

      {row?.closed_on && (
        <Text className="mt-4">
          This case is closed, so nothing further can be recorded on it. A case
          closes by itself when the account is paid up.
        </Text>
      )}

      <Subheading level={4} className="mt-8">
        What has happened
      </Subheading>
      <Text className="mt-1 text-sm">
        Most recent first. Events recorded on the same day are shown in the
        order they must have happened — the exact time is not kept.
      </Text>

      {ordered.length === 0 ? (
        <Text className="mt-2">Nothing recorded yet.</Text>
      ) : (
        <ol className="mt-4 space-y-4">
          {ordered.map((e) => {
            const moved = !!e.status_to && e.status_from !== e.status_to;
            return (
              <li
                key={e.id}
                className="border-l-2 border-zinc-200 dark:border-zinc-700 pl-4"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Badge color={moved ? 'amber' : 'zinc'}>
                    {collectionsEventType.label(e.event_type)}
                  </Badge>
                  {moved && (
                    <Text className="text-sm">
                      {e.status_from
                        ? caseStatus.label(e.status_from)
                        : 'opened'}{' '}
                      → {caseStatus.label(e.status_to)}
                    </Text>
                  )}
                  <Text className="text-sm text-zinc-500 dark:text-zinc-400">
                    {formatBillingDate(e.occurred_on)}
                    {e.created_by ? '' : ' · by the nightly job'}
                  </Text>
                </div>
                <Text className="mt-1">{e.description}</Text>
              </li>
            );
          })}
        </ol>
      )}

      <Dialog open={noting} onClose={() => setNoting(false)} size="xl">
        <DialogTitle>Add a note</DialogTitle>
        <DialogBody>
          <Text className="mb-4">
            For what was said on the phone, what was agreed, what was posted.{' '}
            <strong>A note does not move the case</strong> — recording something
            and deciding to escalate are different acts.
          </Text>
          <Field>
            <Label>Note</Label>
            <Description>Permanent, and part of the record.</Description>
            <Textarea
              rows={4}
              value={note}
              invalid={!!noteError}
              onChange={(e) => {
                setNote(e.target.value);
                setNoteError('');
              }}
            />
            {noteError && <ErrorMessage>{noteError}</ErrorMessage>}
          </Field>
          <ErrorAlert className="mt-4">{noteSaveError}</ErrorAlert>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setNoting(false)} disabled={busy}>
            Cancel
          </Button>
          <Button color="sky" onClick={addNote} disabled={busy}>
            {busy ? 'Saving…' : 'Add note'}
          </Button>
        </DialogActions>
      </Dialog>

      <AdvanceCaseDialog
        caseRow={advancing}
        onClose={() => setAdvancing(null)}
        onDone={load}
      />
    </div>
  );
};

export default CaseEventTrail;
